<template>
  <Starfield>
    <iRow vertical-align="middle" align="center">
      <iColumn align="center" :width="600">
        <iSpace :height="40" />
        <iHeading variant="accent" font-size="displayLarge">
          {{ headline }}
        </iHeading>
        <iSpace :height="16" />
        <iText v-if="!preGranted" font-size="medium">
          <strong>{{ app.display_name }}</strong> wants to act on your behalf
          inside q3po. If you approve, q3po will create a dedicated user
          named "{{ derivedUserPreview }}" in your org and let
          {{ app.display_name }} authenticate as that user from now on.
        </iText>
        <iText v-if="!preGranted && scopesList.length > 0" font-size="small">
          Scopes requested: {{ scopesList.join(", ") }}
        </iText>
        <iText v-if="preGranted" font-size="medium">
          You've already authorized <strong>{{ app.display_name }}</strong>.
          Click below to send a fresh token back to it.
        </iText>
        <iText v-if="errorMessage" variant="error">
          {{ errorMessage }}
        </iText>
        <iSpace :height="24" />
        <iRow width="hug" align="center" gap="medium">
          <iButton
            v-if="!preGranted"
            variant="primary"
            :is-loading="loadingApprove"
            @click="approve"
          >
            Approve
          </iButton>
          <iButton
            v-if="!preGranted"
            variant="secondary"
            :disabled="loadingApprove"
            @click="deny"
          >
            Deny
          </iButton>
          <iButton
            v-if="preGranted"
            variant="primary"
            :is-loading="loadingApprove"
            @click="approve"
          >
            Send token to {{ app.display_name }}
          </iButton>
        </iRow>
      </iColumn>
    </iRow>
  </Starfield>
</template>

<script>
import { $axios } from "@bloglovin/vue-framework";
import Starfield from "@/components/Starfield";
import { sanitizeReturnUrl } from "@/utils/returnUrl.mjs";

export default {
  name: "AppAuthConsent",
  components: { Starfield },
  props: {
    app: { type: Object, required: true },
    appToken: { type: String, required: true },
    returnUrl: { type: String, default: "" },
    preGranted: { type: Boolean, default: false },
  },
  data() {
    return {
      loadingApprove: false,
      errorMessage: "",
    };
  },
  computed: {
    headline() {
      return this.preGranted
        ? `${this.app.display_name} is already authorized`
        : `Authorize ${this.app.display_name}?`;
    },
    derivedUserPreview() {
      const username = this.$blclient?.data?.userData?.username
        ?? this.$blclient?.data?.userData?.first_name
        ?? "you";
      return `${this.app.display_name} for ${username}`;
    },
    scopesList() {
      if (!this.app.scopes) {
        return [];
      }
      return String(this.app.scopes).split(",").map(s => s.trim()).filter(Boolean);
    },
  },
  methods: {
    approve() {
      this.send("approve");
    },
    deny() {
      this.send("deny");
    },
    send(decision) {
      this.loadingApprove = true;
      this.errorMessage = "";
      $axios.post("app-auth/consent", {
        app_token: this.appToken,
        decision,
      }).then(response => {
        const state = response.data?.state;
        if (state === "granted") {
          this.handoffToken(response.data.token);
          return;
        }
        if (state === "denied") {
          this.handoffDenied();
          return;
        }
        this.errorMessage = "Unexpected state: " + (state ?? "unknown");
        this.loadingApprove = false;
      }).catch(error => {
        this.errorMessage = this.parseError(error);
        this.loadingApprove = false;
      });
    },
    handoffToken(token) {
      // Pass the token back to the calling app via return_url. We use
      // a fragment (#token=...) rather than a query string so the
      // token never lands in HTTP referer headers or server access
      // logs en route. The calling app's callback URL reads
      // location.hash, captures the token, and redirects to clear it.
      if (this.returnUrl) {
        window.location.href = this.cleanReturnUrl() + "#token=" + encodeURIComponent(token);
        return;
      }
      // No return_url — render the token for manual copy. Rare path,
      // mostly used by Picard's CLI bootstrap.
      this.errorMessage = "Token issued. No return URL configured.";
      this.loadingApprove = false;
    },
    handoffDenied() {
      if (this.returnUrl) {
        window.location.href = this.cleanReturnUrl() + "?denied=1";
        return;
      }
      this.errorMessage = "Authorization denied.";
      this.loadingApprove = false;
    },
    cleanReturnUrl() {
      // Strip JSON-escaped backslashes so window.location.href doesn't
      // double the path slashes and 404 the callback. Logic lives in
      // utils/returnUrl.js so it can be unit-pinned; see
      // tests/app-auth-return-url.spec.mjs.
      return sanitizeReturnUrl(this.returnUrl);
    },
    parseError(error) {
      const data = error?.response?.data ?? {};
      if (data.error) {
        return String(data.error);
      }
      return "Consent submission failed.";
    },
  },
};
</script>
